<template>
  <form class="space-y-6" @submit.prevent="onSubmit">
    <div class="space-y-6">
      <div v-if="!initialData" class="max-w-md">
        <SelectField
          name="type"
          :label="t('couponType')"
          :options="couponTypeOptions"
          class="w-full"
        />
      </div>

      <template v-if="showPromoFields">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <InputField v-if="!initialData" name="code" :label="t('promoCode')" />
        </div>
      </template>

      <template v-else>
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <InputField
            v-if="!!initialData"
            name="remainingValue"
            :label="t('remainingValue')"
            type="number"
            :min="0"
            :max-fraction-digits="2"
            required
            class="w-full"
            adornment="€"
          />
          <InputField
            name="originalValue"
            :label="t(!!initialData ? 'originalValue' : 'value')"
            type="number"
            :min="0"
            :max-fraction-digits="2"
            required
            class="w-full"
            :disabled="!!initialData"
            adornment="€"
          />
        </div>
      </template>

      <div v-if="showPromoFields" class="grid grid-cols-1 gap-6 md:grid-cols-2">
        <DatepickerField name="validFrom" :label="t('validFrom')" class="w-full" />
        <DatepickerField name="validTo" :label="t('validTo')" class="w-full" />
        <InputField
          name="maxRedemptions"
          :label="t('maxRedemptions')"
          type="number"
          :min="1"
          class="w-full"
        />
        <LabeledText v-if="initialData" :label="t('redemptionCount')">
          {{ initialData.redemptionCount ?? 0 }}
        </LabeledText>
        <div class="flex items-center md:mt-4">
          <CheckboxField
            name="oneRedemptionPerCustomer"
            :label="t('oneRedemptionPerCustomer')"
            class="mt-2"
          />
        </div>
        <div class="flex items-center md:mt-4">
          <CheckboxField name="isActive" :label="t('active')" class="mt-2" />
        </div>
      </div>

      <div v-else-if="initialData" class="grid grid-cols-1 gap-6 md:grid-cols-3">
        <LabeledText :label="t('couponType')">
          {{ t(`couponType.${initialCouponType}`) }}
        </LabeledText>
        <div>
          <DatepickerField name="validTo" :label="t('validTo')" class="w-full" />
        </div>
        <div class="flex items-center md:mt-4">
          <CheckboxField name="isActive" :label="t('active')" class="mt-2" />
        </div>
      </div>

      <div v-if="initialData?.orderInvoice" class="mt-4 rounded-lg bg-white p-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="text-sm text-gray-600">{{ t('orderInvoice') }}</div>
            <div class="text-base font-bold">{{ initialData.orderInvoice?.invoiceNumber }}</div>
          </div>
          <CVButton
            :to="{ name: 'invoice', params: { id: initialData.orderInvoice?.id } }"
            variant="link"
          >
            {{ t('viewInvoice') }}
          </CVButton>
        </div>
      </div>

      <template v-if="!showPromoFields">
        <Divider />
        <CustomerDataForm name="customerData" />
      </template>

      <template v-else>
        <Divider />
        <h2>{{ t('couponBenefits') }}</h2>
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <InputField
            name="EXTRA_KM"
            :label="t('extraKm')"
            adornment="km"
            type="number"
            :min="1"
            class="w-full"
          />
        </div>
      </template>
    </div>

    <Divider />

    <slot name="actions">
      <div class="flex justify-end">
        <CVButton type="submit" variant="default" :loading="isSubmitting">
          {{ submitLabel }}
        </CVButton>
      </div>
    </slot>
  </form>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import type { Coupon } from '@/entities/coupons/coupon.entity';
import { useI18n } from 'vue-i18n';
import InputField from '@/components/InputField.vue';
import CheckboxField from '@/components/CheckboxField.vue';
import SelectField from '@/components/SelectField.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import CustomerDataForm from '@/components/booking-forms/customer/CustomerDataForm.vue';
import Divider from '@/components/Divider.vue';
import LabeledText from '@/components/LabeledText.vue';
import { customerOptionalSchema } from '@/validation/customer.schema';
import * as yup from 'yup';
import DatepickerField from '@/components/DatepickerField.vue';
import { CouponType } from '@carvia/ros-client-types';

const props = defineProps<{
  initialData?: Partial<Coupon>;
  submitLabel?: string;
}>();

const emit = defineEmits<{
  (e: 'submit', data: Partial<Coupon>): void;
}>();

const { t } = useI18n();
const initialCouponType: Coupon['type'] =
  props.initialData?.type ?? (props.initialData ? CouponType.REGULAR : CouponType.GOODWILL);

const isPromoCouponType = (type?: Coupon['type']) => type === CouponType.PROMO;

const createPromoCouponValidationSchema = (isCreate: boolean) =>
  yup.object({
    type: yup.string().oneOf([CouponType.PROMO]).required(),
    code: isCreate
      ? yup
          .string()
          .required()
          .matches(/^[A-Z0-9-]{3,30}$/, t('promoCodeInvalid'))
      : yup.string().optional(),
    EXTRA_KM: yup.number().required().min(1),
    isActive: yup.boolean().required(),
    validFrom: yup.date().nullable().optional(),
    validTo: yup.date().nullable().optional(),
    oneRedemptionPerCustomer: yup.boolean().required(),
    maxRedemptions: yup.number().nullable().min(1).optional(),
  });

const createValueCouponValidationSchema = (isCreate: boolean) =>
  yup.object({
    type: yup.string().oneOf([CouponType.REGULAR, CouponType.GOODWILL]).required(),
    originalValue: yup.number().required().min(0),
    remainingValue: !isCreate ? yup.number().required().min(0) : yup.number().optional(),
    isActive: !isCreate ? yup.boolean().required() : yup.boolean().optional(),
    validTo: yup.date().nullable().optional(),
    customerData: yup.object(customerOptionalSchema(0, new Date())).optional(),
  });

const couponTypeOptions = computed(() => [
  { label: t('regularCoupon'), value: CouponType.REGULAR },
  { label: t('goodwillCoupon'), value: CouponType.GOODWILL },
  { label: t('promoCoupon'), value: CouponType.PROMO },
]);

const couponTypeRef = ref<Coupon['type']>(initialCouponType);

const validationSchema = computed(() => {
  const isCreate = !props.initialData;
  const couponType = props.initialData?.type ?? couponTypeRef.value;

  return isPromoCouponType(couponType)
    ? createPromoCouponValidationSchema(isCreate)
    : createValueCouponValidationSchema(isCreate);
});

const { handleSubmit, isSubmitting, values, setFieldValue } = useForm({
  validationSchema,
  initialValues: {
    type: initialCouponType,
    remainingValue: props.initialData?.remainingValue ?? 0,
    originalValue: props.initialData?.originalValue ?? 0,
    code: props.initialData?.code ?? '',
    EXTRA_KM: props.initialData?.benefits?.EXTRA_KM ?? undefined,
    isActive: props.initialData?.isActive ?? true,
    customerData: props.initialData?.customerData,
    validFrom: props.initialData?.validFrom ?? undefined,
    validTo: props.initialData?.validTo ?? undefined,
    oneRedemptionPerCustomer: props.initialData?.oneRedemptionPerCustomer ?? false,
    maxRedemptions: props.initialData?.maxRedemptions ?? undefined,
  },
});

watch(
  () => values.type,
  (type) => {
    if (type) {
      couponTypeRef.value = type;
    }
  },
  { immediate: true },
);

watch(
  () => values.code,
  (code) => {
    if (!code) {
      return;
    }

    const upperCode = code.toUpperCase();
    if (upperCode !== code) {
      setFieldValue('code', upperCode);
    }
  },
);

const showPromoFields = computed(
  () => isPromoCouponType(initialCouponType) || isPromoCouponType(couponTypeRef.value),
);

const onSubmit = handleSubmit((formValues) => {
  const { EXTRA_KM, ...couponValues } = formValues;
  emit('submit', { ...couponValues, benefits: { EXTRA_KM } });
});
</script>

<i18n lang="json">
{
  "en": {
    "remainingValue": "Remaining Value",
    "active": "Active",
    "customerData": "Customer Data",
    "orderInvoice": "Order Invoice",
    "viewInvoice": "View Invoice",
    "validTo": "Valid To",
    "validFrom": "Valid From",
    "oneRedemptionPerCustomer": "One redemption per customer",
    "maxRedemptions": "Max total redemptions",
    "redemptionCount": "Redemptions used",
    "couponType": "Coupon Type",
    "originalValue": "Original Value",
    "value": "Value",
    "regularCoupon": "Regular voucher",
    "goodwillCoupon": "Goodwill coupon / appeasement",
    "promoCoupon": "Promo coupon",
    "promoCode": "Promo code",
    "extraKm": "Extra kilometers",
    "promoCodeInvalid": "Code must be 3-30 characters (A-Z, 0-9, -)",
    "couponBenefits": "Coupon Benefits"
  },
  "de": {
    "remainingValue": "Verbleibender Wert",
    "active": "Aktiv",
    "customerData": "Kundendaten",
    "orderInvoice": "Bestellungsrechnung",
    "viewInvoice": "Rechnung anzeigen",
    "validTo": "Gültig bis",
    "validFrom": "Gültig ab",
    "oneRedemptionPerCustomer": "Nur einmal pro Kunde",
    "maxRedemptions": "Max. Gesamteinlösungen",
    "redemptionCount": "Einlösungen",
    "couponType": "Gutscheintyp",
    "originalValue": "Originalwert",
    "value": "Wert",
    "regularCoupon": "Regulärer Gutschein",
    "goodwillCoupon": "Kulanzgutschein / Entärgerung",
    "promoCoupon": "Aktionsgutschein",
    "promoCode": "Aktionscode",
    "extraKm": "Extra-Kilometer",
    "promoCodeInvalid": "Code muss 3-30 Zeichen lang sein (A-Z, 0-9, -)",
    "couponBenefits": "Gutschein-Vorteile"
  },
  "es": {
    "remainingValue": "Valor restante",
    "active": "Activo",
    "customerData": "Datos del cliente",
    "orderInvoice": "Pedir factura",
    "viewInvoice": "Ver factura",
    "validTo": "Válido hasta",
    "validFrom": "Válido desde",
    "oneRedemptionPerCustomer": "Una redención por cliente",
    "maxRedemptions": "Máximo de redenciones totales",
    "redemptionCount": "Redenciones usadas",
    "couponType": "Tipo de cupón",
    "originalValue": "Valor original",
    "value": "Valor",
    "regularCoupon": "Cupón normal",
    "goodwillCoupon": "Cupón de cortesía / compensación",
    "promoCoupon": "Cupón promocional",
    "promoCode": "Código promocional",
    "extraKm": "Kilómetros extra",
    "promoCodeInvalid": "El código debe tener 3-30 caracteres (A-Z, 0-9, -)",
    "couponBenefits": "Beneficios del cupón"
  }
}
</i18n>
